import React, { useEffect, useState } from 'react';

import { Container, TablePagination, Tooltip } from '@material-ui/core';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import APIError from 'components/AlertScreens/APIError';
import ListSkeletonLoader from 'components/AlertScreens/ListSkeletonLoader';
import useAsync from 'components/hooks/useAsync';
import { TemplateQuestion } from 'models/templates/review';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Divider1, StyledBox, StyledButton, StyledGrid, StyledPaper } from 'styles/common';

import { QUESTION_ORDER_ACTIONS, REVIEW_QUESTION_TYPES, REVIEW_QUESTION_TYPE_MAP_2 } from 'constants/ProjectConstants';
import { swapElementsInArray } from 'constants/helper';
import { AddCircularIcon } from 'constants/icons';
import theme from 'constants/theme';

import { getOrganizationTags, getRoleBasedCompetenciesEnabled } from 'services/admin';
import { showInfoSnackbar } from 'services/snackbar';
import { getReviewTemplateQuestions } from 'services/templates/review';

import { Typography10, Typography11 } from 'uiToolkit/Typography';

import { useQuestionListSidePanel } from '../../../common/TemplateCommons/QuestionListSidepanel/context/QuestionListSidepanelContext';
import { useReviewTemplate } from '../common/ReviewTemplateContext';
import { ContentContainer, FullHeightBox, WhiteBGStyledGrid } from '../common/styles';
import QuestionRowType1 from './common/QuestionRowType1';

const tagType = 'CORE_COMPETENCIES';
const teamId = -1;

const TOTAL_MAX_WEIGHT = Number(100).toFixed(2);
const TOTAL_MIN_WEIGHT = Number(0).toFixed(2);

const QuestionListHead = () => (
  <StyledBox paddingRight="10px">
    <Divider1 />
    <WhiteBGStyledGrid container justify="space-between" padding="1rem">
      <StyledGrid item xs={4}>
        <Typography10 color="textSecondary" variant="body2">
          Competency Name
        </Typography10>
      </StyledGrid>

      <StyledGrid item xs={4}>
        <Typography10 color="textSecondary" variant="body2">
          Score
        </Typography10>
      </StyledGrid>
      <StyledGrid item xs={4}>
        <Typography10 color="textSecondary" variant="body2">
          Your Feedback
        </Typography10>
      </StyledGrid>
    </WhiteBGStyledGrid>
    <Divider1 />
  </StyledBox>
);

const CompetencyRow = ({ competency, showAddQuestion, addQuestionHandler }) => (
  <StyledBox paddingRight="10px;">
    <Divider1 />
    <StyledGrid
      style={{ background: 'white' }}
      container
      justify="space-between"
      alignItems="center"
      padding="0.5rem 1rem"
    >
      <StyledGrid item xs={4}>
        <FullWidthBox flexcontainer alignitems="Center">
          <Tooltip title={showAddQuestion ? 'add question to select' : ''} placement="top">
            {showAddQuestion ? <CheckBoxOutlineBlankIcon color="disabled" /> : <CheckBoxIcon color="primary" />}
          </Tooltip>
          <Typography10 fontsize="14px" color={theme.palette.background.black.v1} variant="body2" leftGutter={1}>
            {competency.name}
          </Typography10>
        </FullWidthBox>
      </StyledGrid>
      <StyledGrid item xs={2}>
        {showAddQuestion && (
          <FullHeightBox flexcontainer justifycontent="center" alignitems="center">
            <StyledButton
              color="primary"
              startIcon={<AddCircularIcon size="fixedSmallest" color="primary" />}
              onClick={() => addQuestionHandler({ competencyId: competency.id })}
            >
              Add Question
            </StyledButton>
          </FullHeightBox>
        )}
      </StyledGrid>
    </StyledGrid>
    <Divider1 />
  </StyledBox>
);

const CompetencyReview = () => {
  const [selectedId, setSelectedId] = useState({
    competencyId: null,
    index: null,
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filteredCompetencies, setFilteredCompetencies] = useState();

  const { showSidepanel, hideSidepanel } = useQuestionListSidePanel();
  const {
    state: { META, POTENTIAL },
    setQuestions,
    addQuestion,
    removeQuestion,
  } = useReviewTemplate();
  const [totalWeightage, setTotalWeightage] = useState(0);
  const [roleBasedCompetencyEnabled, setRoleBasedCompetencyEnabled] = useState(false);
  const { templateQuestions, competencyList } = POTENTIAL;

  const { data, run, isLoading, isError } = useAsync();
  const dispatch = useDispatch();

  // For pagination
  const filterQuestion = () => {
    const x = page * rowsPerPage;
    const y = x + rowsPerPage;
    const filteredCompKeys = Object.keys(templateQuestions).slice(x, y);
    setFilteredCompetencies(filteredCompKeys);
  };

  useEffect(() => {
    filterQuestion();
  }, [page, templateQuestions, rowsPerPage]);

  const handleRowsPerPageChange = (newRowsValue) => {
    const newValue = Number.parseInt(newRowsValue, 10);
    setRowsPerPage(newValue);
    setPage(0);
  };

  const getDataPromise = () =>
    Promise.all([
      getOrganizationTags({ teamId, tagType }),
      getReviewTemplateQuestions({
        templateId: META.id,
        section: REVIEW_QUESTION_TYPES.POTENTIAL,
      }),
    ]);

  const getData = () => {
    if (META.id) {
      run(getDataPromise());
    }
  };

  const fetchRoleBasedCompetenciesEnabled = () => {
    getRoleBasedCompetenciesEnabled()
      .then((res) => setRoleBasedCompetencyEnabled(res))
      .catch((err) => console.error(err));
  };

  useEffect(() => {
    getData();
    fetchRoleBasedCompetenciesEnabled();
  }, [META]);

  useEffect(() => {
    if (data) {
      setQuestions({
        section: REVIEW_QUESTION_TYPES.POTENTIAL,
        questions: data[1].competencyQuestions,
        competencyList: data[0],
      });
    }
  }, [data]);

  const handleQuestionSelect = ({ competencyId, index }) => {
    setSelectedId({
      competencyId,
      index,
    });
  };

  const onQuestionAddition = ({ question, competencyId, insertionPosition }) => {
    const templateQuestion = TemplateQuestion.fromJSON({
      templateId: META.id,
      question,
      competencyId,
    });
    addQuestion({
      question: templateQuestion,
      competencyId,
      section: REVIEW_QUESTION_TYPES.POTENTIAL,
      insertionPosition,
    });
  };

  const onAddQuestionToAll = ({ question }) => {
    const newTemplateQuestions = {};
    Object.keys(templateQuestions).forEach((key) => {
      const templateQuestion = TemplateQuestion.fromJSON({
        templateId: META.id,
        question,
        competencyId: key,
      });

      // Checks if a question already exists for the competency via add to competency option.
      const isAlreadyAdded = templateQuestions[key]?.filter((ques) => ques.question.id === question.id).length > 0;

      if (isAlreadyAdded) {
        newTemplateQuestions[key] = [...templateQuestions[key]];
      } else {
        newTemplateQuestions[key] = [...templateQuestions[key], templateQuestion];
      }
    });
    setQuestions({ section: REVIEW_QUESTION_TYPES.POTENTIAL, questions: newTemplateQuestions });
  };

  const handleAddQuestion = ({ competencyId, isAddToAll = false, insertionPosition }) => {
    showSidepanel({
      open: true,
      onSuccess: ({ question, insertionPosition }) => {
        if (isAddToAll) {
          onAddQuestionToAll({ question });
          hideSidepanel();
        } else {
          onQuestionAddition({ question, competencyId, insertionPosition });
        }
      },
      // Questions can be of ["GOAL", "COMPETENCY", "QNA"] and we need to show only of COMPETENCY aka POTENTIAL type for sidepanel opened from Competency Review.
      type: REVIEW_QUESTION_TYPE_MAP_2.POTENTIAL,
      // competencyId filteration works only for Competency for ex: sidepanel opened from "RaisesBarAlways" will contain questions related to this tag and without tags but not of other tags.
      competencyId,
      insertionPosition,
    });
  };

  const handleRemoveQuestion = ({ index, competencyId }) => {
    removeQuestion({
      section: REVIEW_QUESTION_TYPES.POTENTIAL,
      index,
      competencyId,
    });
  };

  const handleQuestionDataChange = ({ param, value, index, competencyId }) => {
    const competencyQuestions = templateQuestions[competencyId];

    if (param === 'weightage' && (Number.isNaN(value) || value < 0 || value > 100)) {
      dispatch(showInfoSnackbar('Valid Weight Range is [0,100]'));
      return;
    }

    const newTemplateQuestion = {
      ...competencyQuestions[index],
      [param]: value,
    };

    const updatedQuestions = [...competencyQuestions];
    updatedQuestions[index] = newTemplateQuestion;

    const newTemplateQuestions = { ...templateQuestions };
    newTemplateQuestions[competencyId] = updatedQuestions;

    setQuestions({ section: REVIEW_QUESTION_TYPES.POTENTIAL, questions: newTemplateQuestions });
  };

  const handleQuestionOrderChange = ({ index, action, competencyId }) => {
    const competencyQuestions = templateQuestions[competencyId];
    let newQuestionList = [];

    if (action === QUESTION_ORDER_ACTIONS.MOVE_UP) {
      newQuestionList = swapElementsInArray(competencyQuestions, index - 1, index);
    } else {
      newQuestionList = swapElementsInArray(competencyQuestions, index + 1, index);
    }

    // Map to maintain the displayOrder property
    newQuestionList = newQuestionList.map((item, idx) => ({
      ...item,
      displayOrder: idx + 1,
    }));

    const newTemplateQuestions = { ...templateQuestions };
    newTemplateQuestions[competencyId] = newQuestionList;

    setQuestions({ section: REVIEW_QUESTION_TYPES.POTENTIAL, questions: newTemplateQuestions });
  };

  const calculateWeightage = () => {
    const total = Object.keys(templateQuestions).reduce(
      (acc, curr) =>
        acc +
        templateQuestions[curr].reduce((accu, current) => {
          if (!Number.isNaN(current.weightage)) {
            if (current.question.analyticsQuestionType === 'RATING') {
              return accu + Number(current.weightage);
            }
            return accu;
          }
        }, 0),
      0
    );
    setTotalWeightage(total);
  };
  const totalWeightageFixed = totalWeightage?.toFixed(2);
  useEffect(() => {
    calculateWeightage();
  }, [templateQuestions]);

  return (
    <Container maxWidth="md">
      <ContentContainer transparent>
        <StyledBox paddingRight="10px">
          <StyledGrid container justify="space-between" alignItems="center" bottomGutter={2}>
            <StyledGrid item xs={6}>
              <Typography11>Select Competency for this template</Typography11>
            </StyledGrid>
            <StyledGrid item xs={2}>
              <StyledButton
                customColor="primary"
                borderColor="primary"
                fontColor="white"
                fullWidth
                onClick={() => handleAddQuestion({ isAddToAll: true })}
              >
                Add To All
              </StyledButton>
            </StyledGrid>
          </StyledGrid>
        </StyledBox>
      </ContentContainer>
      <ContentContainer transparent>
        {isError ? (
          <APIError reloadHandler={() => getData()} />
        ) : isLoading ? (
          <ListSkeletonLoader />
        ) : (
          <>
            <QuestionListHead />
            {filteredCompetencies &&
              filteredCompetencies.map((key) => (
                <React.Fragment key={key}>
                  <CompetencyRow
                    competency={competencyList.filter((item) => item.id === key)[0]}
                    key={key}
                    showAddQuestion={templateQuestions[key]?.length <= 0}
                    addQuestionHandler={handleAddQuestion}
                  />
                  {templateQuestions[key].map((item, idx) => (
                    <QuestionRowType1
                      key={item.id || idx}
                      addQuestionHandler={handleAddQuestion}
                      questionObject={item}
                      handleRemoveQuestion={handleRemoveQuestion}
                      handleQuestionDataChange={handleQuestionDataChange}
                      questionOrderChangeHandler={handleQuestionOrderChange}
                      isFirstItem={idx === 0}
                      isLastItem={idx + 1 === templateQuestions[key].length}
                      competencyId={key}
                      index={idx}
                      roleBasedCompetencyEnabled={roleBasedCompetencyEnabled}
                      handleQuestionSelect={handleQuestionSelect}
                      selectedId={selectedId}
                    />
                  ))}
                </React.Fragment>
              ))}
          </>
        )}
        <StyledTablePagination
          component="div"
          count={templateQuestions ? Object.keys(templateQuestions).length : 0}
          page={page}
          rowsPerPage={rowsPerPage}
          SelectProps={{
            inputProps: { 'aria-label': 'competencies per page' },
          }}
          onChangePage={(_, pageNum) => setPage(pageNum)}
          onChangeRowsPerPage={({ target }) => handleRowsPerPageChange(target.value)}
        />
        <TotalWeightageContainer
          padding="1rem"
          variant="outlined"
          flexcontainer
          justifycontent="space-between"
          bgColor="lightGrey"
        >
          <Typography10 style={{ fontSize: '14px' }} color="grey" weight="bold">
            Total weightage
          </Typography10>
          <Typography10
            style={{ fontSize: '24px' }}
            weight="bold"
            color={
              totalWeightageFixed === TOTAL_MAX_WEIGHT || totalWeightageFixed === TOTAL_MIN_WEIGHT ? 'green' : 'red'
            }
            variant="h6"
          >
            {totalWeightageFixed}%
          </Typography10>
        </TotalWeightageContainer>
      </ContentContainer>
    </Container>
  );
};

export default CompetencyReview;

const FullWidthBox = styled(StyledBox)`
  width: 100%;
`;

const TotalWeightageContainer = styled(StyledPaper)`
  margin-right: 10px;
`;

const StyledTablePagination = styled(TablePagination)`
  background: white;
  margin-right: 10px;
`;
